import { TZDateMini } from '@date-fns/tz';
import {
  addHours,
  getDaysInMonth,
  setDate,
  setHours,
  setMilliseconds,
  setMinutes,
  setMonth,
  setSeconds,
  setYear,
  startOfDay,
} from 'date-fns';
import { useCallback, useMemo } from 'react';

import type { Precision } from '../../../core/time/shared.types.js';
import { useDateTimeMessages } from '../../../core/time/useDateTimeMessages.js';
import {
  getDayPeriodValues,
  getSafeRegionalFormat,
} from '../../forms-core/time/helpers.js';
import type { DateTimePickerProps } from '../types.js';

const INVALID_DATE = new TZDateMini('Invalid');

/** Trim any spaces inside a string. */
function trimSpace(str: string) {
  return str.replace(/\s+/g, '');
}

/** Provides functions to convert a date object to its parts and vice versa. */
export function useDateTimeFormat(
  type: DateTimePickerProps['type'] = 'datetime',
  precision: Precision = 'minutes',
) {
  const dayPeriodValues = useMemo(() => getDayPeriodValues(), []);

  const {
    timeZone,
    formatDateToParts,
    formatOptions: format,
  } = useDateTimeMessages({
    type,
    precision,
    locale: getSafeRegionalFormat(),
  });

  /**
   * Convert a date object to its parts for the configured format.
   * Part values are always zoned and based on the configured timezone.
   */
  const toParts = useCallback(
    (date: Date) => formatDateToParts(date, format),
    [format, formatDateToParts],
  );

  /** Convert parts to a UTC date object.  */
  const toDateTime = useCallback(
    (parts: Intl.DateTimeFormatPart[]) => {
      // every non literal part must be empty
      const isEmpty = !parts.some(
        ({ type, value }) => type !== 'literal' && value !== '',
      );

      if (isEmpty) {
        return null;
      }

      // Calculate max nrOfDays for given year and month.
      const year = parts.find((p) => p.type === 'year')?.value;
      const month = parts.find((p) => p.type === 'month')?.value;
      const day = parts.find((p) => p.type === 'day')?.value;
      const maxNrOfDays =
        year !== undefined && month !== undefined
          ? getDaysInMonth(
              new TZDateMini(
                parseInt(year),
                parseInt(month) - 1,
                timeZone ?? 'UTC',
              ),
            )
          : 31;

      // creates an initial date at the start of the current day
      const current = TZDateMini.tz(timeZone ?? 'UTC');
      // take year from parts or the current year
      const yearValue = year ? parseInt(year) : current.getFullYear();
      // take month from parts or the current month
      const monthValue = month ? parseInt(month) - 1 : current.getMonth();
      // take day from parts or the current day
      const dayValue = day ? parseInt(day) : current.getDate();
      const initialDate = startOfDay(
        new TZDateMini(yearValue, monthValue, dayValue, timeZone),
      );

      const dayPeriod = parts.find((part) => part.type === 'dayPeriod');

      const date = parts.reduce((date: Date, part) => {
        const intValue = parseInt(part.value);
        switch (part.type) {
          case 'year':
            // In the Japanese region, the date format is YYYY.MM.DD, and setYear(date, 0) is called first.
            // If a leading 0 is entered, the initialDate becomes '1899-12-31T23:00:00Z'.
            // This can cause the date to underflow to '-000001-12-31', resulting in invalid dates.
            if (intValue === 0) {
              return INVALID_DATE;
            }
            return setYear(date, intValue);
          case 'month':
            // setMonth interprets the leading 0 as December of the previous year.
            if (intValue === 0) {
              return INVALID_DATE;
            }
            return setMonth(date, intValue - 1);
          case 'day':
            // setDay interprets the leading 0 as the 31st of the previous month.
            if (intValue > maxNrOfDays || intValue === 0) {
              return INVALID_DATE;
            }
            return setDate(date, intValue);
          case 'hour':
            if (dayPeriod) {
              // We handle the day period together with the hours as the day period can be defined
              // before or after the hours in different languages. To ensure a proper handling of
              // the hours together with day periods we do this together.
              date = setHours(date, intValue % 12); // Treat 12 as 0 in 12h notation.
              //Some locales do have a space in the day period, e.g. 'a. m.' or 'p. m.'. To compare them we trim the spaces.
              const dayPeriodValue = trimSpace(dayPeriod.value);

              if (
                dayPeriodValue === trimSpace(dayPeriodValues[1]) ||
                dayPeriodValue === 'PM'
              ) {
                date = addHours(date, 12); // If the day period is in the afternoon add 12 hours.
              } else if (
                dayPeriodValue !== trimSpace(dayPeriodValues[0]) &&
                dayPeriodValue !== 'AM'
              ) {
                date = INVALID_DATE; // If the day period doesn't match AM or PM (in any locale), we treat it as invalid date.
              }
              return date;
            }

            return setHours(date, intValue);
          case 'minute':
            return setMinutes(date, intValue);
          case 'second':
            return setSeconds(date, intValue);
          case 'fractionalSecond':
            return setMilliseconds(date, intValue);
          default:
            // Ignore time literals and other part types.
            return date;
        }
        // Already set the month and year of the new date to the month selected to avoid issues when setting the day.
        // If in the current locale, the date format is e.g. <day><month><year>, setting the day may shift to the next
        // month if the current month has less days.
        // e.g.
        //    current month = April
        //    selected day = 31
        //    setDate(31) would shift to May 1, since April only has 30 days
        // FIXME
        // In the 'should not close the overlay when the input is blurred' unit test, for some reason in the 'to' input,
        // the month spin button 'onChange' is triggered when typing '1', even though spin buttons should not even be
        // rendered. The year fallback is to avoid errors generating the new date in this case ('-01-01' is not supported).
        // Doesn't happen in the browser, but may be some state updates somewhere that could cause issues at some point.
      }, initialDate);

      return date;
    },
    [timeZone, dayPeriodValues],
  );

  // provide empty parts for null value in current datetime format
  const emptyParts = useMemo(
    () =>
      toParts(TZDateMini.tz(timeZone ?? 'UTC')).map(({ type, value }) => {
        return { type, value: type === 'literal' ? value : '' };
      }),
    [toParts, timeZone],
  );

  return { format, toParts, toDateTime, emptyParts };
}
